import type {ReactNode} from 'react';
import {alpha, styled} from '@mui/material/styles';
import {Box, Typography} from '@mui/material';
import {Link} from 'react-router-dom';
import {
    AppBar,
    Layout as RALayout,
    CheckForApplicationUpdate,
    TitlePortal,
    AppBarClasses,
    Sidebar,
    Menu,
} from 'react-admin';

const LogoLink = styled(Link)(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.spacing(1),
    padding: theme.spacing(0.5),
    transition: theme.transitions.create(['background-color', 'box-shadow'], {
        duration: theme.transitions.duration.shortest,
    }),
    '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
    },
    '&:focus-visible': {
        outline: `2px solid ${alpha(theme.palette.primary.main, 0.45)}`,
        outlineOffset: 2,
    },
}));

const Logo = styled('img')(({theme}) => ({
    width: 36,
    height: 36,
    display: 'block',
    objectFit: 'contain',
}));

const CustomAppBar = () => (
    <AppBar
        color="inherit"
        elevation={0}
        sx={(theme) => ({
            borderBottom: `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
            backgroundColor: alpha(theme.palette.common.white, 0.9),
            backdropFilter: 'blur(10px)',
            [`& .${AppBarClasses.toolbar}`]: {
                paddingLeft: theme.spacing(2),
                paddingRight: theme.spacing(2),
                gap: theme.spacing(1),
            },
        })}
    >
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                flexShrink: 0,
                mr: 1,
            }}
        >
            <LogoLink to="/" aria-label="BreedBuddies home">
                <Logo src="/favicon.png" alt="" />
            </LogoLink>
            <Box sx={{display: {xs: 'none', sm: 'block'}, minWidth: 0}}>
                <Typography
                    variant="subtitle2"
                    sx={{
                        fontWeight: 700,
                        lineHeight: 1.2,
                        letterSpacing: '-0.01em',
                    }}
                >
                    BreedBuddies
                </Typography>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{display: 'block', lineHeight: 1.2}}
                >
                    Admin
                </Typography>
            </Box>
        </Box>
        <TitlePortal
            className={AppBarClasses.title}
            sx={(theme) => ({
                flex: 1,
                minWidth: 0,
                fontWeight: 700,
                fontSize: {xs: '1.05rem', sm: '1.2rem'},
                letterSpacing: '-0.02em',
                lineHeight: 1.35,
                color: theme.palette.text.primary,
                '& .MuiTypography-root': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                },
            })}
        />
    </AppBar>
);

const BrandedSidebar = styled(Sidebar)(({theme}) => ({
    '& .MuiDrawer-paper': {
        backgroundImage: `linear-gradient(180deg, ${theme.palette.common.white} 0%, ${alpha(
            theme.palette.grey[50],
            0.98
        )} 100%)`,
        borderRight: `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
        boxSizing: 'border-box',
    },
    '& .RaSidebar-fixed': {
        paddingTop: theme.spacing(1.5),
        paddingBottom: theme.spacing(2),
    },
}));

const StyledMenu = styled(Menu)(({theme}) => ({
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(1),
    '& > .MuiMenuItem-root': {
        borderRadius: theme.spacing(1),
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        marginTop: theme.spacing(0.25),
        marginBottom: theme.spacing(0.25),
        paddingTop: theme.spacing(1.1),
        paddingBottom: theme.spacing(1.1),
        transition: theme.transitions.create(
            ['background-color', 'color', 'font-weight'],
            {duration: theme.transitions.duration.shortest}
        ),
        '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
        },
        '&.RaMenuItemLink-active': {
            backgroundColor: alpha(theme.palette.primary.main, 0.14),
            color: theme.palette.primary.main,
            fontWeight: 600,
        },
    },
}));

export const Layout = ({children}: {children: ReactNode}) => (
    <RALayout
        appBar={CustomAppBar}
        sidebar={BrandedSidebar}
        menu={StyledMenu}
    >
        {children}
        <CheckForApplicationUpdate />
    </RALayout>
);
